import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import useApi from "../utils/useApi";
import apis from "../utils/apis";
import { setUserInfo } from "../store/slices/userSlice";
import { isTimeInRange } from "../utils/utils";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

const useAddress = (options = {}) => {
  const { loadDefault = false } = options;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userDefaultAddress = useSelector(
    (state) => state.user.userInfo?.defaultAddress
  );

  const [addresses, setAddresses] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [settingDefaultId, setSettingDefaultId] = useState(null);
  const [isDeletingAddress, setIsDeletingAddress] = useState(false);

  const {
    callApi: callGetAddresses,
    data: addressesData,
    isLoading,
  } = useApi();
  const { callApi: callCreateAddress } = useApi();
  const { callApi: callUpdateAddress } = useApi();
  const { callApi: callDeleteAddress } = useApi();
  const { callApi: callUpdateUserDefaultAddress } = useApi();
  const { callApi: callUserDetails } = useApi();

  useEffect(() => {
    if (addressesData?.length) {
      setAddresses(addressesData || []);
    }
  }, [addressesData]);

  const fetchAddresses = async () => {
    await callGetAddresses({ api: apis.getUserAddresses });
  };

  const fetchUser = async () => {
    try {
      const userDetails = await callUserDetails({ api: apis.getUserDetails });
      const user = userDetails?.user;
      const startTime = user?.defaultAddress?.nearby?.mart?.startTime;
      const endTime = user?.defaultAddress?.nearby?.mart?.endTime;
      const isNotAvailable =
        !user?.defaultAddress?.nearby?.mart?.availabilityStatus;
      const isCurrentTimeNotInMartTimeRange = !isTimeInRange(
        startTime,
        endTime
      );
      user.isMartClose = isCurrentTimeNotInMartTimeRange || isNotAvailable;
      if (user) {
        dispatch(setUserInfo(user));
      }

      localStorage.setItem("defaultLocationId", user?.defaultAddress?._id);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    setIsDeletingAddress(true);
    try {
      await callDeleteAddress({
        api: apis.deleteUserAddress,
        param: addressId,
      });
      await fetchAddresses();
      setShowDeleteModal(false);
      setAddressToDelete(null);
      toast.success(t("addresses.addressDeletedSuccessfully"));
    } catch {
      toast.error(t("addresses.failedToDeleteAddress"));
    } finally {
      setIsDeletingAddress(false);
    }
  };

  const openDeleteModal = (addressId) => {
    setAddressToDelete(addressId);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setAddressToDelete(null);
  };

  const handleSetDefaultAddress = async (addressId) => {
    setSettingDefaultId(addressId);
    try {
      await callUpdateUserDefaultAddress({
        api: apis.updateUserFromUser,
        body: {
          defaultAddress: addressId,
        },
      });
      localStorage.setItem("defaultLocationId", addressId);
      await fetchAddresses();
      await fetchUser();
      toast.success(t("addresses.addressSetAsDefault"));
    } catch {
      toast.error(t("addresses.failedToSetDefaultAddress"));
    } finally {
      setSettingDefaultId(null);
    }
  };

  const resetModal = () => {
    setShowModal(false);
    setSelectedAddress(null);
  };

  const handleAddressComplete = async (address) => {
    try {
      if (selectedAddress) {
        // Editing existing address
        await callUpdateAddress({
          api: apis.updateUserAddress,
          body: address,
          param: selectedAddress._id,
        });
        
        // If marked as default, update user's default address
        if (address.is_default) {
          await callUpdateUserDefaultAddress({
            api: apis.updateUserFromUser,
            body: {
              defaultAddress: selectedAddress._id,
            },
          });
          localStorage.setItem("defaultLocationId", selectedAddress._id);
        }
        
        toast.success(t("addresses.addressUpdatedSuccessfully"));
      } else {
        // Creating new address
        const response = await callCreateAddress({
          api: apis.createUserAddress,
          body: {
            ...address,
            is_default: address?.is_default || !addresses?.length,
          },
        });
        
        // If marked as default, update user's default address with the newly created address ID
        if (address.is_default && response?.address?._id) {
          await callUpdateUserDefaultAddress({
            api: apis.updateUserFromUser,
            body: {
              defaultAddress: response.address._id,
            },
          });
          localStorage.setItem("defaultLocationId", response.address._id);
        }
        
        toast.success(t("addresses.addressAddedSuccessfully"));
      }
      setShowModal(false);
      setSelectedAddress(null);
      await fetchAddresses();
      await fetchUser();
    } catch {
      toast.error(
        selectedAddress
          ? t("addresses.failedToUpdateAddress")
          : t("addresses.failedToAddAddress")
      );
    }
  };

  useEffect(() => {
    if (loadDefault) {
      fetchAddresses();
    }
  }, [loadDefault]);

  return {
    addresses,
    userDefaultAddress,
    isLoading,
    showModal,
    setShowModal,
    selectedAddress,
    setSelectedAddress,
    showDeleteModal,
    addressToDelete,
    settingDefaultId,
    isDeletingAddress,
    fetchAddresses,
    fetchUser,
    handleDeleteAddress,
    openDeleteModal,
    closeDeleteModal,
    handleSetDefaultAddress,
    resetModal,
    handleAddressComplete,
  };
};

export default useAddress;
